import React, { createContext, useContext, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { AppTab, LANDING_BY_ROLE } from '../config/navigation';
import { AdminSection } from '../types/admin';

type PendingAction = 'raise-complaint' | null;

interface NavigationContextValue {
  activeTab: AppTab;
  setActiveTab: (tab: AppTab) => void;
  activeAdminSection: AdminSection;
  setActiveAdminSection: (section: AdminSection) => void;
  openAdminSection: (section: AdminSection) => void;
  pendingAction: PendingAction;
  requestAction: (action: NonNullable<PendingAction>) => void;
  clearAction: () => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<AppTab>(() =>
    user ? LANDING_BY_ROLE[user.role] : 'dashboard'
  );
  const [activeAdminSection, setActiveAdminSection] = useState<AdminSection>('organization');
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const value = useMemo<NavigationContextValue>(
    () => ({
      activeTab,
      setActiveTab,
      activeAdminSection,
      setActiveAdminSection,
      openAdminSection: (section) => {
        setActiveAdminSection(section);
        setActiveTab('admin-config');
      },
      pendingAction,
      requestAction: (action) => setPendingAction(action),
      clearAction: () => setPendingAction(null),
    }),
    [activeTab, activeAdminSection, pendingAction]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) throw new Error('useNavigation must be used within a NavigationProvider');
  return context;
};
